import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { Mail, Eye, EyeOff, ArrowRight, CheckCircle2 } from 'lucide-react'
import { useAuth } from '../../store/authStore'
import { checkUsername, rememberPassword } from '../../lib/api/auth'
import { PageShell } from '../../components/layout/PageShell'
import { Field } from '../../components/ui/Field'
import { Button } from '../../components/ui/Button'
import { googleSignIn, facebookSignIn, googleConfigured, facebookConfigured } from '../../lib/social'
import { isNativePlatform } from '../../lib/platform'

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export function AuthScreen() {
  const { t } = useTranslation()
  const { locale = 'it' } = useParams()
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const status = useAuth((s) => s.status)
  const signIn = useAuth((s) => s.signIn)
  const socialSignIn = useAuth((s) => s.socialSignIn)

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [mode, setMode] = useState<'unknown' | 'login' | 'register'>('unknown')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [resetSent, setResetSent] = useState(false)
  const [socialLoading, setSocialLoading] = useState<'google' | 'facebook' | null>(null)

  const redirect = params.get('redirect') || `/${locale}/bookings`

  // already signed in → leave the auth screen
  if (status === 'authenticated') return <Navigate to={redirect} replace />

  const emailValid = EMAIL_RE.test(email)

  async function onEmailBlur() {
    setMode('unknown')
    if (!emailValid) return
    const exists = await checkUsername(email)
    setMode(exists ? 'login' : 'register')
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    if (!emailValid) {
      setError(t('auth.invalidInput'))
      return
    }
    // Resolve whether the account exists (the email blur may not have run yet).
    let exists = mode === 'login'
    if (mode === 'unknown') {
      setSubmitting(true)
      exists = await checkUsername(email.trim())
      setSubmitting(false)
      setMode(exists ? 'login' : 'register')
    }
    // Unknown email → send to the proper registration flow. The app never auto-registers
    // through login (which would create an account, bypassing the activation email).
    if (!exists) {
      navigate(`/${locale}/register?email=${encodeURIComponent(email.trim())}`)
      return
    }
    // Existing account → real login.
    if (password.length < 6) {
      setError(t('auth.invalidInput'))
      return
    }
    setSubmitting(true)
    try {
      const res = await signIn(email.trim(), password)
      if (res.ok) navigate(redirect, { replace: true })
      else setError(t('auth.loginFailedActivate'))
    } catch {
      setError(t('auth.loginFailedActivate'))
    } finally {
      setSubmitting(false)
    }
  }

  async function onForgot() {
    if (!emailValid) {
      setError(t('auth.emailFirst'))
      return
    }
    await rememberPassword(email.trim())
    setResetSent(true)
    setError(null)
  }

  async function onSocial(provider: 'google' | 'facebook') {
    setError(null)
    setSocialLoading(provider)
    try {
      const token = provider === 'google' ? await googleSignIn() : await facebookSignIn()
      const res = await socialSignIn(provider === 'google' ? { google_token: token } : { fb_token: token })
      if (res.ok) navigate(redirect, { replace: true })
      else setError(t('auth.invalid'))
    } catch (e) {
      setError(e instanceof Error ? e.message : t('auth.invalid'))
    } finally {
      setSocialLoading(null)
    }
  }

  const submitLabel =
    mode === 'login' ? t('auth.login') : mode === 'register' ? t('auth.register') : t('auth.continue')

  return (
    <PageShell narrow>
      <div className="gsm-card p-6 sm:p-8">
        <h1 className="font-display text-2xl font-extrabold tracking-tight text-ink-900">{t('auth.title')}</h1>
        <p className="mt-1.5 text-sm text-ink-500">{t('auth.subtitle')}</p>

        {mode !== 'unknown' && (
          <p className="mt-4 rounded-xl bg-ink-50 px-3.5 py-2.5 text-[13px] text-ink-500">
            {mode === 'login' ? t('auth.hintLogin') : t('auth.hintRegister')}
          </p>
        )}

        <form onSubmit={onSubmit} className="mt-5 space-y-4">
          <Field
            label={t('auth.email')}
            type="email"
            name="email"
            autoComplete="email"
            inputMode="email"
            placeholder={t('auth.emailPlaceholder')}
            value={email}
            onChange={(e) => {
              setEmail(e.target.value)
              setResetSent(false)
            }}
            onBlur={onEmailBlur}
            rightSlot={<Mail className="mr-1.5 h-4 w-4 text-ink-300" />}
          />
          {/* password only for existing accounts — new emails register on a dedicated page,
              where the password is set via the activation email */}
          {mode !== 'register' && (
            <Field
              label={t('auth.password')}
              type={showPassword ? 'text' : 'password'}
              name="password"
              autoComplete="current-password"
              placeholder={t('auth.passwordPlaceholder')}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              rightSlot={
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? t('auth.hidePassword') : t('auth.showPassword')}
                  className="mr-1 flex h-8 w-8 items-center justify-center rounded-lg text-ink-400 transition hover:text-ink-700"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              }
            />
          )}

          {error && (
            <p className="rounded-xl bg-brand-50 px-3.5 py-2.5 text-[13px] font-medium text-brand-700">{error}</p>
          )}

          {resetSent && (
            <p className="flex items-center gap-2 rounded-xl bg-green-50 px-3.5 py-2.5 text-[13px] font-medium text-green-700">
              <CheckCircle2 className="h-4 w-4" /> {t('auth.resetSent')}
            </p>
          )}

          <Button type="submit" size="lg" className="w-full" loading={submitting} rightIcon={<ArrowRight className="h-4 w-4" />}>
            {submitLabel}
          </Button>
        </form>

        <div className="mt-4 flex flex-wrap items-center justify-between gap-2">
          <button onClick={onForgot} className="text-sm font-medium text-brand-600 hover:text-brand-800">
            {t('auth.forgot')}
          </button>
          <button
            type="button"
            onClick={() => navigate(`/${locale}/register${emailValid ? `?email=${encodeURIComponent(email.trim())}` : ''}`)}
            className="text-sm font-medium text-ink-500 hover:text-ink-900"
          >
            {t('auth.noAccount')} <span className="font-semibold text-brand-600">{t('auth.registerCta')}</span>
          </button>
        </div>

        {/* Native webviews can't run Google/Facebook JS login (providers block embedded user agents),
            so the buttons only show on the real web. Native would need native social-login plugins. */}
        {!isNativePlatform() && (googleConfigured || facebookConfigured) && (
          <>
            <div className="my-5 flex items-center gap-3 text-[11px] font-semibold uppercase tracking-wide text-ink-300">
              <span className="h-px flex-1 bg-ink-200" /> {t('common.or')} <span className="h-px flex-1 bg-ink-200" />
            </div>
            <div className="space-y-2.5">
              {googleConfigured && (
                <button
                  type="button"
                  onClick={() => onSocial('google')}
                  disabled={socialLoading !== null}
                  className="flex w-full items-center justify-center gap-2.5 rounded-xl border border-ink-200 bg-white px-4 py-3 text-sm font-semibold text-ink-700 transition hover:bg-ink-50 disabled:opacity-50"
                >
                  <GoogleIcon /> {socialLoading === 'google' ? '…' : t('auth.socialGoogle')}
                </button>
              )}
              {facebookConfigured && (
                <button
                  type="button"
                  onClick={() => onSocial('facebook')}
                  disabled={socialLoading !== null}
                  className="flex w-full items-center justify-center gap-2.5 rounded-xl border border-ink-200 bg-white px-4 py-3 text-sm font-semibold text-ink-700 transition hover:bg-ink-50 disabled:opacity-50"
                >
                  <FacebookIcon /> {socialLoading === 'facebook' ? '…' : t('auth.socialFacebook')}
                </button>
              )}
            </div>
          </>
        )}
      </div>

      <p className="mt-5 px-2 text-center text-xs text-ink-400">{t('auth.disclaimer')}</p>
    </PageShell>
  )
}

function GoogleIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-5 w-5" aria-hidden>
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.76h3.56c2.08-1.92 3.28-4.74 3.28-8.09Z" />
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.56-2.76c-.98.66-2.23 1.06-3.72 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23Z" />
      <path fill="#FBBC05" d="M5.84 14.11A6.6 6.6 0 0 1 5.49 12c0-.73.13-1.45.35-2.11V7.05H2.18A11 11 0 0 0 1 12c0 1.77.42 3.45 1.18 4.95l3.66-2.84Z" />
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.05l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38Z" />
    </svg>
  )
}

function FacebookIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-5 w-5" aria-hidden>
      <path
        fill="#1877F2"
        d="M24 12a12 12 0 1 0-13.875 11.854v-8.385H7.078V12h3.047V9.356c0-3.008 1.792-4.669 4.533-4.669 1.313 0 2.686.235 2.686.235v2.953H15.83c-1.49 0-1.955.925-1.955 1.874V12h3.328l-.532 3.469h-2.796v8.385A12.002 12.002 0 0 0 24 12Z"
      />
    </svg>
  )
}
